import { createBrowserRouter } from "react-router-dom";
import { App } from "./App";
import { HomePage } from "@/features/home";
import { AboutPage } from "@/features/about";
import { ContactPage } from "@/features/contact";
import { LendersPage } from "@/features/lenders";
import { NotFoundPage } from "@/features/errors";
import { PrivacyPolicyPage, TermsConditionsPage, ComplaintsPage, DisclaimerPage } from "@/features/legal";
import {
  CalculatorsPage,
  StampDutyCalculatorPage,
  RepaymentCalculatorPage,
  BorrowingPowerCalculatorPage,
  ExtraRepaymentCalculatorPage,
  RefinanceCalculatorPage,
  LMICalculatorPage,
  PropertyBudgetCalculatorPage,
} from "@/features/calculators";
import { RatesPage } from "@/features/rates";
import {
  GuidesPage,
  LoanProcessGuidePage,
  FirstHomeBuyerGuidePage,
  WhyUseBrokerPage,
  RefinancingGuidePage,
  DocumentChecklistPage,
} from "@/features/guides";
import {
  HomeLoansPage,
  BuyingAHomePage,
  FirstHomeBuyerPage,
  RefinancingPage,
  InvestmentPropertyPage,
  ConstructionLoansPage,
  BridgingLoansPage,
} from "@/features/home-loans";
import {
  BusinessLoansPage,
  AssetFinancePage,
  InvoiceFinancePage,
  TermLoansPage,
} from "@/features/business-loans";
import { ProtectedRoute } from "@/components/common";
import { LoginPage, VerifyPage } from "@/features/auth";
import {
  PortalLayout,
  DashboardPage,
  DocumentsPage,
  ProfilePage,
  PreferencesPage,
  FactFinderPage,
} from "@/features/portal";
import {
  AdminLayout,
  AdminDashboardPage,
  CustomersPage,
  CustomerDetailPage,
  LeadsPage,
  AdminDocumentsPage,
  SubscribersPage,
  BlogManagerPage,
  BlogEditorPage,
  RatesManagerPage,
  CampaignsPage,
  CampaignEditorPage,
  TestimonialsPage,
  FactFindersPage,
  FactFinderDetailPage,
  AutomationsPage,
  ScheduledEmailsPage,
  DatabasePage,
  AnalyticsPage,
  RBADataPage,
  CronLogsPage,
} from "@/features/admin";
import { BlogPage, BlogPostPage } from "@/features/blog";
import { MarketNewsPage } from "@/features/market";
import { UnsubscribePage, PreferencesPage as SubscribePreferencesPage } from "@/features/subscribe";
import { PublicFactFinderPage, FactFinderSuccessPage } from "@/features/fact-finder";

export const router = createBrowserRouter([
  {
    path: "/",
    element: <App />,
    children: [
      // Home
      {
        index: true,
        element: <HomePage />,
      },

      // Home Loans
      {
        path: "home-loans",
        children: [
          {
            index: true,
            element: <HomeLoansPage />,
          },
          {
            path: "buying-a-home",
            element: <BuyingAHomePage />,
          },
          {
            path: "first-home-buyer",
            element: <FirstHomeBuyerPage />,
          },
          {
            path: "refinancing",
            element: <RefinancingPage />,
          },
          {
            path: "investment",
            element: <InvestmentPropertyPage />,
          },
          {
            path: "construction",
            element: <ConstructionLoansPage />,
          },
          {
            path: "bridging",
            element: <BridgingLoansPage />,
          },
        ],
      },

      // Business Loans
      {
        path: "business-loans",
        children: [
          {
            index: true,
            element: <BusinessLoansPage />,
          },
          {
            path: "asset-finance",
            element: <AssetFinancePage />,
          },
          {
            path: "invoice-finance",
            element: <InvoiceFinancePage />,
          },
          {
            path: "term-loans",
            element: <TermLoansPage />,
          },
        ],
      },

      // Calculators
      {
        path: "calculators",
        children: [
          {
            index: true,
            element: <CalculatorsPage />,
          },
          {
            path: "borrowing-power",
            element: <BorrowingPowerCalculatorPage />,
          },
          {
            path: "repayments",
            element: <RepaymentCalculatorPage />,
          },
          {
            path: "stamp-duty",
            element: <StampDutyCalculatorPage />,
          },
          {
            path: "extra-repayments",
            element: <ExtraRepaymentCalculatorPage />,
          },
          {
            path: "refinance",
            element: <RefinanceCalculatorPage />,
          },
          {
            path: "lmi",
            element: <LMICalculatorPage />,
          },
          {
            path: "property-budget",
            element: <PropertyBudgetCalculatorPage />,
          },
        ],
      },

      // Rates (admin only)
      {
        path: "rates",
        element: (
          <ProtectedRoute requireAdmin>
            <RatesPage />
          </ProtectedRoute>
        ),
      },

      // Market News
      {
        path: "market-news",
        element: <MarketNewsPage />,
      },

      // Guides
      {
        path: "guides",
        children: [
          {
            index: true,
            element: <GuidesPage />,
          },
          {
            path: "loan-process",
            element: <LoanProcessGuidePage />,
          },
          {
            path: "first-home-buyer-guide",
            element: <FirstHomeBuyerGuidePage />,
          },
          {
            path: "refinancing-guide",
            element: <RefinancingGuidePage />,
          },
          {
            path: "why-use-a-broker",
            element: <WhyUseBrokerPage />,
          },
          {
            path: "document-checklist",
            element: <DocumentChecklistPage />,
          },
        ],
      },

      // Blog
      {
        path: "blog",
        children: [
          {
            index: true,
            element: <BlogPage />,
          },
          {
            path: ":slug",
            element: <BlogPostPage />,
          },
        ],
      },

      // Static Pages
      {
        path: "about",
        element: <AboutPage />,
      },
      {
        path: "contact",
        element: <ContactPage />,
      },
      {
        path: "lenders",
        element: <LendersPage />,
      },

      // Legal
      {
        path: "privacy-policy",
        element: <PrivacyPolicyPage />,
      },
      {
        path: "terms-conditions",
        element: <TermsConditionsPage />,
      },
      {
        path: "complaints",
        element: <ComplaintsPage />,
      },
      {
        path: "disclaimer",
        element: <DisclaimerPage />,
      },

      // Auth
      {
        path: "login",
        element: <LoginPage />,
      },
      {
        path: "login/verify",
        element: <VerifyPage />,
      },

      // Customer Portal (protected)
      {
        path: "portal",
        element: (
          <ProtectedRoute>
            <PortalLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <DashboardPage />,
          },
          {
            path: "documents",
            element: <DocumentsPage />,
          },
          {
            path: "profile",
            element: <ProfilePage />,
          },
          {
            path: "preferences",
            element: <PreferencesPage />,
          },
          {
            path: "fact-finder",
            element: <FactFinderPage />,
          },
        ],
      },

      // Admin (protected - admin only)
      {
        path: "admin",
        element: (
          <ProtectedRoute requireAdmin>
            <AdminLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <AdminDashboardPage />,
          },
          {
            path: "customers",
            element: <CustomersPage />,
          },
          {
            path: "customers/:id",
            element: <CustomerDetailPage />,
          },
          {
            path: "leads",
            element: <LeadsPage />,
          },
          {
            path: "testimonials",
            element: <TestimonialsPage />,
          },
          {
            path: "fact-finders",
            element: <FactFindersPage />,
          },
          {
            path: "fact-finders/:id",
            element: <FactFinderDetailPage />,
          },
          {
            path: "documents",
            element: <AdminDocumentsPage />,
          },
          {
            path: "subscribers",
            element: <SubscribersPage />,
          },
          {
            path: "blog",
            element: <BlogManagerPage />,
          },
          {
            path: "blog/new",
            element: <BlogEditorPage />,
          },
          {
            path: "blog/edit/:id",
            element: <BlogEditorPage />,
          },
          {
            path: "rates",
            element: <RatesManagerPage />,
          },
          {
            path: "campaigns",
            element: <CampaignsPage />,
          },
          {
            path: "campaigns/new",
            element: <CampaignEditorPage />,
          },
          {
            path: "campaigns/edit/:id",
            element: <CampaignEditorPage />,
          },
          {
            path: "automations",
            element: <AutomationsPage />,
          },
          {
            path: "scheduled-emails",
            element: <ScheduledEmailsPage />,
          },
          {
            path: "analytics",
            element: <AnalyticsPage />,
          },
          {
            path: "rba-data",
            element: <RBADataPage />,
          },
          {
            path: "cron-logs",
            element: <CronLogsPage />,
          },
          {
            path: "database",
            element: <DatabasePage />,
          },
        ],
      },

      // Unsubscribe & Preferences (token-based)
      {
        path: "unsubscribe",
        element: <UnsubscribePage />,
      },
      {
        path: "preferences",
        element: <SubscribePreferencesPage />,
      },

      // Public Fact Finder (token-based, no auth required)
      {
        path: "fact-finder/:token",
        element: <PublicFactFinderPage />,
      },
      {
        path: "fact-finder/success",
        element: <FactFinderSuccessPage />,
      },

      // 404
      {
        path: "*",
        element: <NotFoundPage />,
      },
    ],
  },
]);
